/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { isRightToLeft } from 'js/lib/language';

import { Grid, IconButton, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import _t from 'i18n!nls/front-page';

const styles = {
  banner: css`
    padding-top: var(--cds-spacing-150);
    padding-bottom: var(--cds-spacing-150);
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;

    ${breakpoints.down('md')} {
      padding-left: var(--cds-spacing-600);
      padding-right: var(--cds-spacing-600);
    }

    ${breakpoints.down('sm')} {
      align-items: flex-start;
    }
  `,
  lightBlue: css`
    background-color: var(--cds-color-blue-25);
  `,
  blue: css`
    background-color: var(--cds-color-blue-50);
  `,
  purple: css`
    background-color: var(--cds-color-purple-50);
  `,
  content: css`
    text-align: center;
    width: 100%;
  `,
  dismiss: css`
    position: absolute;

    ${breakpoints.down('md')} {
      right: var(--cds-spacing-100);
      margin-top: calc(var(--cds-spacing-150) * -1);
    }
  `,
  dismissLTR: css`
    right: var(--cds-spacing-300);
  `,
  dismissRTL: css`
    left: var(--cds-spacing-300);
    right: unset;
  `,
};

type Props = {
  variant?: 'blue' | 'purple' | 'light-blue';
};

const RegionalPromotionFullWidthBanner: React.LegacyFunctionComponentWithChildren<Props> = ({ children, variant }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'promo_bar',
      },
    }
  );

  const isRTL = isRightToLeft(_t.getLocale());

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  if (isDismissed) {
    return null;
  }

  let backgroundColor = styles.blue;
  if (variant === 'purple') {
    backgroundColor = styles.purple;
  }
  if (variant === 'light-blue') {
    backgroundColor = styles.lightBlue;
  }

  return (
    <section css={[styles.banner, backgroundColor]} aria-label={_t('Promotional content')} ref={visibilityTrackingRef}>
      <div css={styles.content}>
        <PageGridContainer>
          <Grid item xs={12}>
            {children}
          </Grid>
        </PageGridContainer>
      </div>
      <div css={[styles.dismiss, isRTL ? styles.dismissRTL : styles.dismissLTR]}>
        <IconButton intent="close" variant="ghost" onClick={handleDismiss} />
      </div>
    </section>
  );
};

export { RegionalPromotionFullWidthBanner };

export default RegionalPromotionFullWidthBanner;
